import * as THREE from 'three'
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js'
import { skyTexture, sunGlowTexture } from './textures'
import { ENV_HDR_BASE64 } from './envmap'
import { setHazeSun } from './atmosphere'
import {
  EffectComposer, RenderPass, EffectPass,
  BloomEffect, VignetteEffect, NoiseEffect, SMAAEffect,
} from 'postprocessing'
import { detectQuality, tierSettings } from './quality'
import { QualityGovernor } from './qualityGovernor'
import type { QualitySettings } from './quality'

/**
 * A one-shot context purely to read the GPU string. Released immediately —
 * browsers cap the number of live WebGL contexts, and holding a second one for
 * the life of the page can cost the real renderer its own.
 */
function probeGl(): WebGL2RenderingContext | undefined {
  try {
    const c = document.createElement('canvas')
    const gl = c.getContext('webgl2') ?? undefined
    queueMicrotask(() => gl?.getExtension('WEBGL_lose_context')?.loseContext())
    return gl
  } catch {
    return undefined
  }
}

/**
 * Renderer, scene, sky and lighting.
 *
 * This is no longer the spec §10 setup. That called for "low-poly,
 * vertex-coloured terrain, baked directional lighting, no PBR, no real-time
 * shadows beyond a blob" to hold 60fps on a three-year-old Android — a
 * deliberate trade that has been deliberately reversed. What replaces it:
 *
 *   - **filmic tonemapping**, so highlights roll off instead of clipping. Snow
 *     lives at the top of the range and the old linear clamp burned 10-22% of
 *     the slope to flat white, erasing exactly the relief everything else was
 *     trying to show.
 *   - **image-based lighting** from the sky itself, so shadowed snow picks up
 *     cold sky-blue while lit snow stays warm. This is most of why real snow
 *     photographs the way it does, and no amount of hemisphere-light tinting
 *     fakes it convincingly.
 *   - **a real sun shadow**, which is what actually reveals form. An unshadowed
 *     snowfield reads as a flat sheet no matter how good the shading is.
 */
/**
 * Direction from the slope toward the sun — low and off the looker's left,
 * about 27° above the horizon.
 *
 * The previous 50° sun lit the face almost head-on, which is why the terrain
 * measured flat (contrast SD 14.5) and self-shadowed across 0.2% of the frame:
 * a slope lit from directly above has barely any variation in incidence angle
 * and casts almost nothing. A raking sun is both how alpine light actually
 * behaves for most of a day and the single cheapest source of form — every
 * convexity gets a lit face and a shaded one for free.
 */
const SUN_DIR = new THREE.Vector3(-0.62, 0.47, -0.63).normalize()
/** How far up that direction the light rides — sets the shadow frustum only. */
const SUN_DISTANCE = 320

export class World {
  quality: QualitySettings

  // --- Adaptive quality ----------------------------------------------------
  /** Owns the decision; this class only owns applying it. */
  private governor: QualityGovernor
  readonly renderer: THREE.WebGLRenderer
  /** Built lazily on the first render, torn down on tier change. */
  private composer: EffectComposer | null = null
  readonly scene = new THREE.Scene()
  readonly sun: THREE.DirectionalLight
  private readonly pmrem: THREE.PMREMGenerator
  private envTarget: THREE.WebGLRenderTarget | null = null

  /** Half-width of the sun's shadow frustum, metres. */
  private readonly shadowExtent = 190

  constructor(readonly canvas: HTMLCanvasElement) {
    // Probe the GPU on a throwaway context before building the real one:
    // `antialias` is fixed at construction, so the tier has to be known first.
    this.quality = detectQuality(probeGl())
    this.governor = new QualityGovernor(this.quality.tier, this.quality.forced)

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: this.quality.msaa,
      powerPreference: 'high-performance',
      stencil: false,
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatio))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    // Snow sits at the very top of the range, so how the top of the range is
    // handled matters more here than in almost any other subject.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.20

    this.renderer.shadowMap.enabled = true
    // PCFSoft is deprecated in this three version and silently falls back to
    // PCFShadowMap, so ask for what is actually being used. Surfaced only once
    // the screenshot harness started listening to console.error — three
    // reports deprecations there, and the harness had been watching the wrong
    // CDP channel and reporting "no console errors" throughout.
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    this.renderer.setClearColor(0xdfeaf5)

    // --- Sky ---------------------------------------------------------------
    // Starts as the procedural gradient (instant, no pop of black), and swaps
    // to the real HDRI panorama once it arrives over the network.
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(2400, 24, 16),
      new THREE.MeshBasicMaterial({
        map: skyTexture(), side: THREE.BackSide, fog: false,
        depthWrite: false, depthTest: false,
      }),
    )
    sky.frustumCulled = false
    sky.renderOrder = -100
    this.scene.add(sky)
    this.loadRealSky(sky)

    // The sun itself. Additive, unfogged, and pinned along SUN_DIR so the disc,
    // the haze that brightens toward it and the direction light actually
    // arrives from all agree — which is what makes an outdoor scene read as
    // having a real light source rather than an ambient wash.
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: sunGlowTexture(),
      blending: THREE.AdditiveBlending,
      depthWrite: false, depthTest: false, fog: false,
      transparent: true, opacity: 0.95,
    }))
    glow.scale.setScalar(760)
    glow.position.copy(SUN_DIR).multiplyScalar(2100)
    glow.renderOrder = -99
    glow.frustumCulled = false
    this.scene.add(glow)

    this.scene.fog = new THREE.Fog(0xdfeaf5, 340, 1500)

    // --- Environment -------------------------------------------------------
    // Prefilter the sky into an irradiance/radiance probe. Shadowed snow then
    // reads blue because it is genuinely lit by the sky, not because a
    // hemisphere light was tinted to fake it.
    this.pmrem = new THREE.PMREMGenerator(this.renderer)
    this.pmrem.compileEquirectangularShader()
    this.buildEnvironment()

    // --- Sun ---------------------------------------------------------------
    this.sun = new THREE.DirectionalLight(0xfff4e2, 5.0)
    this.sun.castShadow = true
    this.sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize)
    const cam = this.sun.shadow.camera
    cam.left = -this.shadowExtent
    cam.right = this.shadowExtent
    cam.top = this.shadowExtent
    cam.bottom = -this.shadowExtent
    // The light rides close to the skier rather than a kilometre out. A
    // directional light's *position* only sets where its shadow frustum sits,
    // not the direction of its light, so there is nothing to gain from placing
    // it far away — and plenty to lose: the near/far range has to span the
    // distance, which throws away depth precision across the whole slope.
    cam.near = 10
    cam.far = SUN_DISTANCE * 2
    cam.updateProjectionMatrix()
    this.sun.shadow.bias = -0.0004
    this.sun.shadow.normalBias = 0.06
    // Shadows lift rather than crush. At full strength the finer 1.8m terrain
    // self-shadows across two thirds of the frame and a third of the snow fell
    // below usable brightness. Raising exposure to compensate just pushes
    // everything into the tonemap shoulder and flattens it again — lifting the
    // shadows themselves keeps the contrast and the brightness.
    this.sun.shadow.intensity = 0.72
    this.scene.add(this.sun)
    this.scene.add(this.sun.target)
    setHazeSun(SUN_DIR)
    this.followSun(0, 0, 0)

    // A little residual fill so deep shadow does not go black. The environment
    // does most of this work now, so it is much weaker than it used to be.
    this.scene.add(new THREE.HemisphereLight(0xcfe2f5, 0xb4c2d2, 0.16))
  }

  /**
   * Build the PMREM probe used as scene.environment from a real HDRI.
   *
   * This replaced a hand-built stand-in — a gradient dome, a bright sphere for
   * the sun, and a pale hemisphere underneath for snow bounce. That was three
   * guesses at what a sky does. A photograph of an actual snowfield knows the
   * answer: how much bluer the zenith is than the horizon, how much light the
   * snow throws back up, and how the two meet.
   *
   * The source is 1024x512 and 1.3MB; what ships is 256x128 and 171KB base64,
   * inlined as a module so the single-file build still needs no network. That
   * survives because irradiance is low-frequency — see tools/make-envmap.mjs.
   *
   * Note the *visible* sky is untouched: the gradient dome and the sun sprite
   * still draw the background, tuned as they are against the fog. This changes
   * the light, not the view. A 256px equirect would look like a smear if it
   * were on screen.
   */
  private buildEnvironment(): void {
    const raw = atob(ENV_HDR_BASE64)
    const bytes = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
    const hdr = new HDRLoader().parse(bytes.buffer)

    const equirect = new THREE.DataTexture(
      hdr.data as unknown as Float32Array,
      hdr.width, hdr.height, hdr.format, hdr.type,
    )
    equirect.mapping = THREE.EquirectangularReflectionMapping
    equirect.colorSpace = THREE.LinearSRGBColorSpace
    equirect.needsUpdate = true

    this.envTarget?.dispose()
    this.envTarget = this.pmrem.fromEquirectangular(equirect)
    this.scene.environment = this.envTarget.texture
    equirect.dispose()
    // Balanced against the sun by measurement: at full strength the sky filled
    // every shadow back in and the terrain measured flat (SD 12.6). Pulling it
    // back doubles the contrast while keeping deep shadow to ~8% of the snow —
    // push the sun further and three quarters of the face goes dark, which is
    // not what a sunlit snowfield looks like.
    //
    // Left at 0.65 through the switch to a real HDRI, against the instinct to
    // re-tune. A/B at one frozen frame, old probe vs HDRI, identical geometry:
    //
    //   probe         mean    SD   shadow tint (blue-minus-red, darkest 25%)
    //   procedural   191.5  25.4   40.5
    //   HDRI @0.65   198.2  23.8   51.5
    //   HDRI @0.30   191.6  25.7   10.4
    //
    // Dropping to 0.30 matches the old luminance almost exactly — and throws
    // away the entire point, because the sky colour reaching the shadows falls
    // to a quarter. The whole reason for an HDRI is that middle column, so the
    // 3% of brightness and 6% of contrast is the price and it is worth paying.
    //
    // One thing worth knowing: the hand-built probe was *more* saturated than
    // reality. A real snowfield bounces a lot of near-neutral white up from
    // below, so a photograph is less blue overall than the stylised gradient
    // dome it replaced — but what blue there is lands where it should.
    this.scene.environmentIntensity = 0.65
  }

  /**
   * Swap the gradient dome for the actual HDRI, and rebuild the light probe
   * from the same pixels.
   *
   * For most of this project the visible sky and the light were two different
   * things: a procedural gradient you could see, and a 256px probe (baked from
   * the horn-koppe_snow HDRI) you could not. That was a single-file-size
   * constraint, and it is gone — the game is online now, so the full 2K
   * panorama ships as a plain fetch. Once loaded, the sky you look at and the
   * light on the snow are literally the same photograph, which is a
   * correctness of mood no amount of tuning two separate systems ever quite
   * reaches.
   *
   * The one real subtlety is the sun. The scene's light rides SUN_DIR and every
   * shadow agrees with it; the photograph has its own sun wherever the
   * photographer stood. So the texture is spun about the vertical to put its
   * brightest column at SUN_DIR's azimuth — found by scanning the loaded
   * pixels rather than hard-coding an offset, so swapping the HDRI for a
   * different one keeps working.
   */
  private loadRealSky(dome: THREE.Mesh): void {
    new HDRLoader().load('tex/sky.hdr', (tex) => {
      const img = tex.image as { data: Float32Array | Uint16Array; width: number; height: number }

      // Find the sun: brightest column, weighted by a few rows around the
      // brightest texel so a single hot pixel cannot win.
      const { width, height } = img
      const data = img.data
      const stride = 4
      let bestU = 0
      let bestLum = -1
      for (let x = 0; x < width; x++) {
        let lum = 0
        for (let y = 0; y < height; y++) {
          const o = (y * width + x) * stride
          lum += Number(data[o]) + Number(data[o + 1]) + Number(data[o + 2])
        }
        if (lum > bestLum) { bestLum = lum; bestU = x / width }
      }
      // SphereGeometry maps u=0 at +x sweeping toward +z; the dome is viewed
      // from inside, which flips apparent handedness, hence the sign. Verified
      // against the sun sprite, which rides SUN_DIR directly.
      const azimuth = Math.atan2(SUN_DIR.z, SUN_DIR.x)
      const wantU = 1 - (azimuth / (2 * Math.PI) + 0.5)
      tex.wrapS = THREE.RepeatWrapping
      tex.offset.x = bestU - wantU
      tex.colorSpace = THREE.LinearSRGBColorSpace
      tex.needsUpdate = true

      const mat = dome.material as THREE.MeshBasicMaterial
      mat.map?.dispose()
      mat.map = tex
      mat.needsUpdate = true

      // Same pixels, better probe: the embedded 256px bake got us on screen
      // instantly; the 2K rebuild replaces it the moment it is available.
      const equirect = tex.clone()
      equirect.mapping = THREE.EquirectangularReflectionMapping
      equirect.offset.x = 0
      const target = this.pmrem.fromEquirectangular(equirect)
      this.envTarget?.dispose()
      this.envTarget = target
      this.scene.environment = target.texture
    })
  }

  /**
   * Step the tier down if the device cannot hold the frame budget.
   *
   * The tier is picked from a GPU string, a core count and a pixel ratio, and
   * that guess was wrong the first time it met real hardware: it put a Galaxy
   * S22 Ultra on ultra, which measured 50fps. A guess about a device is worth
   * much less than a measurement of it, and the measurement is free — the
   * frame time is right there.
   *
   * The policy lives in QualityGovernor, which is pure and tested; this only
   * applies what it decides.
   */
  adaptQuality(frameDt: number): void {
    const next = this.governor.frame(frameDt)
    if (!next) return
    this.quality = tierSettings(next)
    this.applyQuality()
    console.info(`[fall line] frame budget missed — quality down to ${next} ` +
      `(pixel ratio ${this.quality.pixelRatio})`)
  }

  /**
   * Push the current tier's settings at the renderer.
   *
   * Pixel ratio and shadow map size only. detailFadeStart/End are baked into
   * the snow material's uniforms when TerrainMesh builds it, so a step-down
   * does not move them — the fade stays wherever the *starting* tier put it.
   * Noted rather than quietly left, because pixel ratio is the dominant term by
   * a wide margin and re-plumbing the material for the rest is a bigger change
   * than the frame it would buy.
   */
  private applyQuality(): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatio))
    // Multisampling is fixed at composer construction, so a tier change tears
    // it down; the next render rebuilds it with the new settings.
    this.composer?.dispose()
    this.composer = null
    this.resize()
    if (this.sun.shadow.mapSize.x !== this.quality.shadowMapSize) {
      this.sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize)
      // The map is allocated at its old size; drop it so three rebuilds it.
      this.sun.shadow.map?.dispose()
      this.sun.shadow.map = null as unknown as THREE.WebGLRenderTarget
    }
  }

  resize(): void {
    this.renderer.setSize(window.innerWidth, Math.max(window.innerHeight, 1), false)
    this.composer?.setSize(window.innerWidth, Math.max(window.innerHeight, 1))
  }

  get aspect(): number {
    return window.innerWidth / Math.max(window.innerHeight, 1)
  }

  /**
   * Keep the sun rig and its shadow frustum centred on the action. The frustum
   * is deliberately tight — a shadow map stretched over the whole slope has no
   * resolution left for the ground you are actually looking at.
   */
  followSun(x: number, y: number, z: number): void {
    this.sun.target.position.set(x, y, z)
    this.sun.position.set(
      x + SUN_DIR.x * SUN_DISTANCE,
      y + SUN_DIR.y * SUN_DISTANCE,
      z + SUN_DIR.z * SUN_DISTANCE,
    )
    this.sun.target.updateMatrixWorld()
    this.sun.updateMatrixWorld()
  }

  render(camera: THREE.Camera): void {
    if (!this.composer) this.buildComposer(camera)
    // The composer makes several internal draw calls per frame, and each one
    // resets renderer.info by default — the HUD was reporting the final
    // full-screen quad as the whole scene: 1 draw, 0k triangles. Reset
    // manually at frame start so the counters cover everything.
    this.renderer.info.autoReset = false
    this.renderer.info.reset()
    this.composer!.render()
  }

  /**
   * The post stack. For most of this project the frame went out raw; a graded
   * frame is most of the difference between "rendered" and "shot".
   *
   *   - **Bloom.** Sunlit snow and the sparkle glints genuinely dazzle in
   *     life; a thresholded bloom is that dazzle. This is also what makes the
   *     per-cell sparkle in the snow shader read as light rather than as
   *     white pixels.
   *   - **Vignette + fine grain.** The two cheapest photographic tells there
   *     are. Kept subtle enough that neither survives being pointed at.
   *   - **Anti-aliasing** rides the composer now: multisampled buffers where
   *     the tier has MSAA, SMAA where it does not (the low tier), never both.
   *
   * Deliberately LDR: materials keep ACES tone mapping and the effects run on
   * the graded image. Bloom-before-tonemap is the purer pipeline, but it needs
   * float buffers everywhere and buys a subtlety this game does not sell.
   */
  private buildComposer(camera: THREE.Camera): void {
    const composer = new EffectComposer(this.renderer, {
      multisampling: this.quality.msaa ? 4 : 0,
    })
    composer.addPass(new RenderPass(this.scene, camera))

    const bloom = new BloomEffect({
      luminanceThreshold: 0.72,
      luminanceSmoothing: 0.18,
      intensity: 0.42,
      mipmapBlur: true,
    })
    const vignette = new VignetteEffect({ offset: 0.26, darkness: 0.32 })
    const grain = new NoiseEffect({ premultiply: true })
    grain.blendMode.opacity.value = 0.14

    const effects: import('postprocessing').Effect[] = [bloom, vignette, grain]
    if (!this.quality.msaa) effects.unshift(new SMAAEffect())
    composer.addPass(new EffectPass(camera as THREE.PerspectiveCamera, ...effects))
    composer.setSize(window.innerWidth, Math.max(window.innerHeight, 1))
    this.composer = composer
  }
}
